import { sendToApi } from 'api/client'
import { AIJobStatus } from 'types'
import { StoreSnapshot, TLRecord } from '@tldraw/tldraw'

export enum ChatMessageRole {
  User = 'user',
  Assistant = 'assistant',
}

export interface ChatTextContent {
  type: 'text'
  text: string
}

export interface ChatImageContent {
  type: 'image'
  image_url: string
  media_file_key?: string
}

interface BaseProcessingFields {
  type: 'processing'
  text?: string
}

export interface ProcessingStep {
  name: string
  status: 'in_progress' | 'completed'
}

export interface BaseChatProcessingContent extends BaseProcessingFields {}

export interface ReviseDesignProcessingContent extends BaseProcessingFields {
  tool: 'revise_design'
  width: number
  height: number
  count: number
}

export interface GeneratePhotoProcessingContent extends BaseProcessingFields {
  tool: 'generate_photo'
  steps?: ProcessingStep[]
}

export interface GenerateVideoProcessingContent extends BaseProcessingFields {
  tool: 'generate_video'
  steps?: ProcessingStep[]
}

export type ChatProcessingContent =
  | BaseChatProcessingContent
  | ReviseDesignProcessingContent
  | GeneratePhotoProcessingContent
  | GenerateVideoProcessingContent

export interface ChatCaptionsContent {
  type: 'captions'
  content: string[]
  design_id: string
}

export interface ChatDesignPreviewContent {
  type: 'design_content'
  content: Array<StoreSnapshot<any>>
  width: number
  height: number
  design_id: string
}

export type ChatUnsupportedRequestNavigation =
  | 'design'
  | 'brand_kit_styles'
  | 'brand_kit_content_preferences'
  | 'posting'
  | 'integrations'
  | 'create_new'

export interface ChatUnsupportedRequestContent {
  type: 'unsupported_request'
  navigation?: ChatUnsupportedRequestNavigation[]
  design_id?: string
}

export interface DesignerChatContext {
  design_id: string
}

export type ChatContextItem = DesignerChatContext

export interface ChatContextContent {
  type: 'context'
  context: ChatContextItem
}

export interface ChatRatingContent {
  type: 'rated'
  rating: number | 'upvote' | 'downvote'
}

export interface ChatGeneratedImageContent {
  type: 'generated_image'
  image_url?: string
  slide_image_urls?: string[]
  media_file_id?: string
  design_id: string
  slide_index?: number
  generation_data?: Record<string, unknown>
  action?: 'remove_slide' | 'add_slide' | 'reorder_slides'
  removed_slide_index?: number
  added_slide_index?: number
  candidate_snapshot?: StoreSnapshot<TLRecord>
}

export interface ChatGeneratedVideoContent {
  type: 'generated_video'
  video_url: string
  thumbnail_url: string
  media_file_id: string
  design_id: string
  generation_data?: Record<string, unknown>
}

export type ChatGeneratedMediaContent = ChatGeneratedImageContent | ChatGeneratedVideoContent

export interface ChatInsufficientCreditsContent {
  type: 'insufficient_credits'
  credits_required: number
}

export interface ChatCreditUsageContent {
  type: 'credit_usage'
  credits_used: number
}

export interface ChatGenerationStepsContent {
  type: 'generation_steps'
  tool: 'generate_photo' | 'generate_video'
  steps: ProcessingStep[]
}

export interface ChatComparisonResolvedContent {
  type: 'comparison_resolved'
  action: 'accepted' | 'rejected'
  chat_message_id?: number
}

export interface ChatSuggestedFollowupsContent {
  type: 'suggested_followups'
  suggestions: string[]
}

export type ChatMessageContent =
  | ChatTextContent
  | ChatImageContent
  | ChatProcessingContent
  | ChatContextContent
  | ChatDesignPreviewContent
  | ChatCaptionsContent
  | ChatRatingContent
  | ChatUnsupportedRequestContent
  | ChatGeneratedImageContent
  | ChatGeneratedVideoContent
  | ChatInsufficientCreditsContent
  | ChatCreditUsageContent
  | ChatGenerationStepsContent
  | ChatComparisonResolvedContent
  | ChatSuggestedFollowupsContent

export interface ChatMessage {
  id: number
  thread_id: number
  role: ChatMessageRole
  content: Array<ChatMessageContent>
  created_at: string
  updated_at: string
  status?: AIJobStatus
}

export interface ChatThread {
  id: number
  name: string | null
  messages: ChatMessage[]
  created_at: string
  updated_at: string
  original_design_content?: any
  original_caption?: string
}

export interface PaginatedChatParams {
  page: number
  items: number
}

export const ChatClient = {
  createThread: async (workspaceId: number, name?: string, content?: ChatMessageContent[], fileId?: string) => {
    const res = await sendToApi<{ thread: ChatThread }>(`/w/${workspaceId}/threads`, {
      method: 'post',
      body: { name, content, almanac_file_id: fileId },
    })

    return res
  },

  getThread: async (workspaceId: number, id: number) => {
    const res = await sendToApi<{ thread: ChatThread }>(`/w/${workspaceId}/threads/${id}`, {
      method: 'get',
    })

    return res
  },

  updateThread: async (workspaceId: number, id: number, updatedThread: Partial<ChatThread>) => {
    const res = await sendToApi<{ thread: ChatThread }>(`/w/${workspaceId}/threads/${id}`, {
      method: 'put',
      body: updatedThread,
    })

    return res
  },

  deleteThread: async (workspaceId: number, id: number) => {
    const res = await sendToApi<void>(`/w/${workspaceId}/threads/${id}`, {
      method: 'delete',
    })

    return res
  },

  createMessage: async (workspaceId: number, threadId: number, content: ChatMessageContent[]) => {
    const res = await sendToApi<{ messages: ChatMessage[] }>(`/w/${workspaceId}/threads/${threadId}/messages`, {
      method: 'post',
      body: { content },
    })

    return res
  },

  retryMessage: async (workspaceId: number, threadId: number, messageId: number) => {
    const res = await sendToApi<{ messages: ChatMessage[] }>(
      `/w/${workspaceId}/threads/${threadId}/messages/${messageId}/retry`,
      {
        method: 'post',
      },
    )

    return res
  },

  abortMessage: async (workspaceId: number, threadId: number, messageId: number) => {
    const res = await sendToApi<{ messages: ChatMessage[] }>(
      `/w/${workspaceId}/threads/${threadId}/messages/${messageId}/abort`,
      {
        method: 'post',
      },
    )

    return res
  },

  deleteMessage: async (workspaceId: number, threadId: number, messageId: number) => {
    const res = await sendToApi<void>(`/w/${workspaceId}/threads/${threadId}/messages/${messageId}`, {
      method: 'delete',
    })

    return res
  },

  rateMessage: (
    messageId: number,
    rating: number | 'upvote' | 'downvote',
    feedback?: { issueType?: string; comment?: string },
  ) =>
    sendToApi(`/rate_chat_message`, {
      method: 'POST',
      body: {
        chat_message_id: messageId,
        rating,
        ...(feedback?.issueType && { issue_type: feedback.issueType }),
        ...(feedback?.comment && { comment: feedback.comment }),
      },
    }),

  trackAction: (
    workspaceId: number,
    threadId: number,
    action: {
      action_type: string
      chat_message_id?: number
      metadata?: Record<string, unknown>
      add_chat_message?: boolean
    },
  ) =>
    sendToApi(`/w/${workspaceId}/threads/${threadId}/actions`, {
      method: 'POST',
      body: action,
    }),
}
